import { Grid, useMediaQuery, useTheme } from '@mui/material';
import {
  JobType,
  PanelRootContext,
  PropertyGroup,
  Status,
  TraxeroActionResponse,
} from 'types';
import React, { ReactNode, useContext, useEffect, useMemo, ChangeEvent } from 'react';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { useFetcher, useNavigate, useOutletContext, useRevalidator } from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import { ArrowBack, Close } from '@mui/icons-material';
import { AppContext } from 'context/appContext';
import { PropertyGroupsActionHandler } from './PropertyGroupLayout';
import { ValidatorForm } from 'react-material-ui-form-validator';
import FormFrame from 'components/layout/FormFrame';
import DetailsHeader from 'components/layout/DetailsHeader';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import AuturaTextField from 'components/inputs/AuturaTextField';
import SettingsSection from 'components/layout/SettingsSection';
import AuturaStateField from 'components/inputs/AuturaStateField';
import { useTranslation } from 'react-i18next';
import AuturaDropdown from 'components/inputs/AuturaDropdown';
import { JobTypeChecklist } from 'components/inputs/JobTypeChecklist';
import { useFetcherKey } from 'hooks/useFetcherKey';

export const PropertyGroupDetails = (): ReactNode => {
  const { state } = useContext(AppContext);
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const revalidator = useRevalidator();
  const navigate = useNavigate();
  const displayToast = useContext(ToastContext);
  const { t } = useTranslation();
  const isMobile = useMediaQuery(useTheme().breakpoints.down('lg'));

  const { entity: propertyGroup, setEntity: setPropertyGroup } =
    useOutletContext<PanelRootContext<PropertyGroup>>();

  useEffect(() => {
    if (propertyGroup.data?.settings?.requirePropertyManagerSignature) {
      setPropertyGroup({
        ...propertyGroup,
        data: {
          ...propertyGroup.data,
          settings: {
            ...propertyGroup.data.settings,
            requirePMApproval: [JobType.TOW],
            requirePropertyManagerSignature: false, // remove this in 3.4
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    if (fetcher.data) {
      const response =
        fetcher.data as TraxeroActionResponse<PropertyGroupsActionHandler>;
      if (response?.status < 300) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('update_success', {
            ns: 'toasts',
            name: `${propertyGroup.name}`,
          }),
        });
        revalidator.revalidate();
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('update_error', {
            ns: 'toasts',
            name: `${propertyGroup.name}`,
          }),
        });
      }
    }
  }, [fetcher.data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'name':
        setPropertyGroup({ ...propertyGroup, name: value });
        break;
      case 'description':
        setPropertyGroup({ ...propertyGroup, description: value });
        break;
      case 'externalID':
        setPropertyGroup({ ...propertyGroup, externalID: value });
        break;
      case 'street':
        if (propertyGroup.location) {
          setPropertyGroup({
            ...propertyGroup,
            location: { ...propertyGroup.location, street: value },
          });
        } else {
          setPropertyGroup({
            ...propertyGroup,
            location: { id: 0, street: value },
          });
        }
        break;
      case 'city':
        if (propertyGroup.location) {
          setPropertyGroup({
            ...propertyGroup,
            location: { ...propertyGroup.location, city: value },
          });
        } else {
          setPropertyGroup({
            ...propertyGroup,
            location: { id: 0, city: value },
          });
        }
        break;
      case 'postalCode':
        if (propertyGroup.location) {
          setPropertyGroup({
            ...propertyGroup,
            location: { ...propertyGroup.location, postalCode: value },
          });
        } else {
          setPropertyGroup({
            ...propertyGroup,
            location: { id: 0, postalCode: value },
          });
        }
        break;
      case 'unit':
        if (propertyGroup.location) {
          setPropertyGroup({
            ...propertyGroup,
            location: { ...propertyGroup.location, unit: value },
          });
        } else {
          setPropertyGroup({
            ...propertyGroup,
            location: { id: 0, unit: value },
          });
        }
        break;
      default:
        break;
    }
  };
  const handleSubmit = () => {
    fetcher.submit(
      {
        body: JSON.stringify(propertyGroup),
        handler: PropertyGroupsActionHandler.UPDATE,
        tenantID: state.tenant.id,
        groupID: propertyGroup.id,
      },
      {
        method: 'PUT',
        encType: 'application/json',
      },
    );
  };
  const handleCancelAction = () => {
    if (isMobile) {
      navigate(routeWithParams(ROUTES.PROPERTY_GROUPS, [state.tenant.id]));
    } else {
      revalidator.revalidate();
    }
  };
  const cancelButtonText = isMobile ? t('dashboard') : t('cancel');
  const cancelButtonIcon = useMemo(() => {
    return isMobile ? <ArrowBack /> : <Close />;
  }, [isMobile]);

  const handleSetNotifyPM = (jobTypes: JobType[]) => {
    if (!propertyGroup) return;
    setPropertyGroup({
      ...propertyGroup,
      data: {
        ...propertyGroup.data,
        settings: { ...propertyGroup.data.settings, notifyPM: jobTypes },
      },
    });
  };
  const handleSetRequirePMApproval = (jobTypes: JobType[]) => {
    if (!propertyGroup) return;
    setPropertyGroup({
      ...propertyGroup,
      data: {
        ...propertyGroup.data,
        settings: {
          ...propertyGroup.data.settings,
          requirePMApproval: jobTypes,
        },
      },
    });
  };

  return (
    <ValidatorForm onSubmit={handleSubmit} instantValidate={false}>
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={t('details')}
          onCancel={handleCancelAction}
          submitLabel={t('buttons.update', {
            name: t('property_group.label', { ns: 'entities' }),
          })}
          cancelButtonLabel={cancelButtonText}
          cancelButtonIcon={cancelButtonIcon}
          readonly={!state.entityPermissions.property_group.update}
          disableSubmit={fetcher.state !== 'idle'}
        />
        <ScrollableFormContainer maxHeight='calc(100dvh - 454px)' mobileMaxHeight={254 + TAB_LAYOUT_HEADER_HEIGHT}>
          <SettingsSection title={''}>
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <AuturaTextField
                  name='name'
                  label={t('property_group.name', { ns: 'entities' })}
                  value={propertyGroup.name}
                  onChange={handleChange}
                  required
                  readonly={!state.entityPermissions.property_group.update}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='description'
                  label={t('property_group.description', { ns: 'entities' })}
                  value={propertyGroup.description}
                  onChange={handleChange}
                  readonly={!state.entityPermissions.property_group.update}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaDropdown
                  name='status'
                  label={t('property_group.status', { ns: 'entities' })}
                  value={propertyGroup.status}
                  setValue={(e: ChangeEvent<HTMLInputElement>) => {
                    setPropertyGroup({
                      ...propertyGroup,
                      status: e.target.value as Status,
                    });
                  }}
                  options={[Status.Active, Status.Disabled]}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('property_group.location.label', { ns: 'entities' })}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='street'
                  label={t('property_group.location.street', {
                    ns: 'entities',
                  })}
                  value={propertyGroup.location?.street || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='city'
                  label={t('property_group.location.city', { ns: 'entities' })}
                  value={propertyGroup.location?.city || ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaStateField
                  label={t('property_group.location.stateCode', {
                    ns: 'entities',
                  })}
                  stateCode={propertyGroup.location?.stateCode || ''}
                  setStateCode={(stateCode: string) => {
                    setPropertyGroup({
                      ...propertyGroup,
                      location: { ...propertyGroup.location, stateCode },
                    });
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 3,
                }}
              >
                <AuturaTextField
                  name='postalCode'
                  label={t('property_group.location.postalCode', {
                    ns: 'entities',
                  })}
                  value={propertyGroup.location?.postalCode || ''}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </SettingsSection>
          <SettingsSection
            title={t('subheadings.settings', {
              ns: 'properties',
            })}
            divider={false}
          >
            <Grid container width={'100%'} spacing={1.5}>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <JobTypeChecklist
                  label={t('property.data.settings.notifyPM', {
                    ns: 'entities',
                  })}
                  jobTypes={propertyGroup?.data?.settings?.notifyPM || []}
                  enabledJobTypes={[JobType.TOW, JobType.PATROL, JobType.TAG]}
                  setJobTypes={handleSetNotifyPM}
                  helperText={t('helpers.notify_manager', {
                    ns: 'properties',
                  })}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  lg: 6,
                }}
              >
                <JobTypeChecklist
                  label={t('property.data.settings.requirePMApproval', {
                    ns: 'entities',
                  })}
                  jobTypes={
                    propertyGroup?.data?.settings?.requirePMApproval || []
                  }
                  enabledJobTypes={[JobType.TOW, JobType.TAG]}
                  setJobTypes={handleSetRequirePMApproval}
                  helperText={t('helpers.require_signature', {
                    ns: 'properties',
                  })}
                />
              </Grid>
            </Grid>
          </SettingsSection>
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};
